import { useState, useEffect } from "react";
import InputField from "../../../components/ui/InputField";
import { Button } from "../../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PasswordStrengthIndicator from "../../../components/PasswordStrengthIndicator";
import { checkPasswordStrength } from "../../../utils/passwordStrength";
import { Eye, EyeOff } from "lucide-react";

// Roles whose entire purpose is being scoped to one branch - a branch pick
// is mandatory for these, not just an optional restriction like it is for
// org-wide roles (admin/ops/...).
const BRANCH_REQUIRED_ROLES = ["receptionist", "branch admin"];

export default function Register(onRegistered) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone_number: "",
    role_id: "",
    branch_id: "",
  });
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const selectedRole = roles.find((r) => String(r.id) === String(formData.role_id));
  const branchRequired = BRANCH_REQUIRED_ROLES.includes((selectedRole?.name || "").toLowerCase());

  const handleRegister = async (e) => {
    e.preventDefault();   // prevent page reload

    if (branchRequired && !formData.branch_id) {
      setError(`Please select a branch - ${selectedRole.name} accounts must belong to exactly one branch.`);
      return;
    }
    setError("");

    // Validate password strength
    const strengthCheck = checkPasswordStrength(formData.password);
    if (!strengthCheck.isValid) {
      setPasswordError("Password does not meet strength requirements. Please check the requirements below.");
      return;
    }
    setPasswordError("");

    try {
      const response = await api.registerUser(formData);
      if (response?.status == 201) {
        navigate("/user");
      } else {
        setError(response.data || "Email already exists.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Registration failed");
    }
  };

  useEffect(() => {
    loadRoles();
    loadBranches();
  }, []);

  const loadRoles = async () => {
    try {
        setLoading(true);
        const res = await api.fetchRoles();
        setRoles(res.data);
    } catch (err) {
        setError("Failed to load roles: " + err.message);
    } finally {
        setLoading(false);
    }
};

  const loadBranches = async () => {
    try {
        const res = await api.fetchOrgBranches();
        setBranches(res.data || []);
    } catch (err) {
        console.error("Failed to load branches:", err);
    }
};

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto mb-4 px-2">
        <button
          type="button"
          onClick={() => navigate("/user")}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back to Users</span>
        </button>
      </div>
      <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-secondary-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-secondary-100 flex items-center gap-3">
          <div className="w-9 h-9 bg-primary-100 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-secondary-900">Create Account</h2>
            <p className="text-xs text-secondary-400">Add a new user</p>
          </div>
        </div>
        <form onSubmit={handleRegister} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <InputField label="Name" type="text" name="name" value={formData.name} onChange={handleChange} data-label="Registration Name Input" />
          <InputField label="Email" type="email" name="email" value={formData.email} onChange={handleChange} data-label="Registration Email Input" />
          
          <div className="mb-5 flex flex-col justify-start">
            <label htmlFor="password" className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm pr-10"
                required
                data-label="Registration Password Input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                onClick={() => setShowPassword((prev) => !prev)}
                data-label="Toggle Password Visibility"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <PasswordStrengthIndicator password={formData.password} />
            {passwordError && (
              <p className="text-xs text-red-600 mt-1">{passwordError}</p>
            )}
            <p className="text-xs text-secondary-500 mt-1">
              Must be at least 8 chars with uppercase, lowercase, number, and special char
            </p>
          </div>
          
          <InputField label="Phone Number" type="text" name="phone_number" value={formData.phone_number} onChange={handleChange} data-label="Registration Phone Input" />
          <div className="mb-5 flex flex-col justify-start">
              <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select name="role_id" onChange={handleChange} className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.role_id} required data-label="Registration Role Select">
                <option value="">Select Role</option>
                {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
              </select>
          </div>
          {branches.length > 0 && (
            <div className="mb-5 flex flex-col justify-start">
              <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                Restrict to Branch {branchRequired && <span className="text-red-500">*</span>}
              </label>
              <select
                name="branch_id"
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm ${branchRequired && !formData.branch_id ? "border-red-300" : "border-secondary-200"}`}
                value={formData.branch_id}
                required={branchRequired}
                data-label="Registration Branch Select"
              >
                <option value="">{branchRequired ? "Select a branch..." : "All Branches"}</option>
                {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
              </select>
              <p className={`text-xs mt-1 ${branchRequired ? "text-red-500 font-medium" : "text-secondary-500"}`}>
                {branchRequired
                  ? `${selectedRole.name} accounts must be assigned to exactly one branch.`
                  : "Leave as \"All Branches\" for staff who need org-wide access (e.g. admins). Pick a branch to limit this user to only that location's doctors and appointments (e.g. a receptionist)."}
              </p>
            </div>
          )}
          <div className="col-span-1 md:col-span-2 flex justify-end mt-6 pt-6 border-t border-gray-100 gap-3">
            <button
              type="button"
              onClick={() => navigate("/user")}
              className="px-5 py-2.5 border border-secondary-200 text-secondary-600 rounded-lg text-sm font-semibold hover:bg-secondary-50 transition-colors"
            >
              Cancel
            </button>
            <button type="submit" data-label="Register Submit" className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors">Register</button>
          </div>
        </form>
        {error && <p style={{ color: "red" }}>{error}</p>}
      </div>
    </DashboardLayout>
  );
}